/**
 * Конфігурація для кожного сайту/домену
 * Тут зберігаються унікальні налаштування: reCAPTCHA, GTM, реклама, тощо
 */

export interface SiteConfig {
  // Основні налаштування
  domain: string;
  language: string;
  region: string;
  name: string;
  legid: string; // 🆕 ID для CRM API

  // Google Services
  recaptcha: {
    siteKey: string;
    enabled: boolean;
  };

  gtm: {
    containerId: string;
    enabled: boolean;
  };

  // Google Analytics
  analytics?: {
    measurementId: string;
    enabled: boolean;
  };

  // Google Ads
  googleAds?: {
    conversionId: string;
    enabled: boolean;
  };

  // Facebook Pixel
  facebookPixel?: {
    pixelId: string;
    enabled: boolean;
  };

  // Додаткові налаштування
  contact: {
    phone: string;
    email: string;
    address: string;
  };

  // Соціальні мережі
  social: {
    facebook: string;
    instagram: string;
    telegram: string;
    whatsapp: string;
  };
}

export const SITE_CONFIGS: Record<string, SiteConfig> = {
  // ========== НІМЕЧЧИНА (hixa.eu) ==========
  "hixa.eu": {
    domain: "hixa.eu",
    language: "de",
    region: "DE",
    name: "Deutschland",
    legid: "1",

    recaptcha: {
      siteKey: "6LcobusrAAAAANH9H7-CiqugwqaggkcjNMFF7ByW",
      enabled: true,
    },

    gtm: {
      containerId: "GTM-KMWRPZRF",
      enabled: true,
    },

    analytics: {
      measurementId: "G-XXXXXXXXXX",
      enabled: false,
    },

    googleAds: {
      conversionId: "AW-XXXXXXXXX",
      enabled: false,
    },

    facebookPixel: {
      pixelId: "XXXXXXXXXXXXXXX",
      enabled: false,
    },

    contact: {
      phone: "+49 30 520 151 91",
      email: "kunden@hixa.eu",
      address: "Neukirchstraße 74, 13089 Berlin",
    },

    social: {
      facebook: "https://www.facebook.com/hixa.eu",
      instagram: "https://www.instagram.com/hixa.eu",
      telegram: "https://t.me/HIXA_Tronicker_Bot",
      whatsapp: "https://api.whatsapp.com/send?phone=491734524264",
    },
  },

  // ========== ПОЛЬЩА (poland.hixa.eu) ==========
  "poland.hixa.eu": {
    domain: "poland.hixa.eu",
    language: "pl",
    region: "PL",
    name: "Polska",
    legid: "5",

    recaptcha: {
      siteKey: "6LcobusrAAAAANH9H7-CiqugwqaggkcjNMFF7ByW",
      enabled: true,
    },

    gtm: {
      containerId: "GTM-KMWRPZRF",
      enabled: true,
    },

    contact: {
      phone: "+48 573 568 818",
      email: "support@hixa.pl",
      address: "ul. Edwarda Dembowskiego 10 71-533 Szczecin, Polska",
    },

    social: {
      facebook:
        "https://www.facebook.com/share/vgMb9iCPojwM4SW1/?mibextid=LQQJ4d",
      instagram: "https://www.instagram.com/hixa.pl",
      telegram: "https://t.me/Hixapl",
      whatsapp: "https://wa.me/48573568818",
    },
  },

  // ========== УКРАЇНА (ukraine.hixa.eu) ==========
  "ukraine.hixa.eu": {
    domain: "ukraine.hixa.eu",
    language: "ua",
    region: "UA",
    name: "Україна",
    legid: "9",

    recaptcha: {
      siteKey: "6LcobusrAAAAANH9H7-CiqugwqaggkcjNMFF7ByW",
      enabled: true,
    },

    gtm: {
      containerId: "GTM-KMWRPZRF",
      enabled: true,
    },

    contact: {
      phone: "+380936006842",
      email: "hixaua@gmail.com",
      address: "",
    },

    social: {
      facebook: "#",
      instagram: "https://www.instagram.com/hixa.ua",
      telegram: "#",
      whatsapp: "#",
    },
  },

  // ========== ЧЕХІЯ (czechia.hixa.eu) ==========
  "czechia.hixa.eu": {
    domain: "czechia.hixa.eu",
    language: "cz",
    region: "CZ",
    name: "Česko",
    legid: "11",

    recaptcha: {
      siteKey: "6LcobusrAAAAANH9H7-CiqugwqaggkcjNMFF7ByW",
      enabled: true,
    },

    gtm: {
      containerId: "GTM-KMWRPZRF",
      enabled: true,
    },

    contact: {
      phone: "",
      email: "",
      address: "Nademlejnská 600/1,Praha 14 - Hloubětín, 198 00",
    },

    social: {
      facebook: "#",
      instagram: "#",
      telegram: "#",
      whatsapp: "#",
    },
  },

  // ========== LOCALHOST DE (для розробки) ==========
  localhost: {
    domain: "localhost",
    language: "de",
    region: "DE",
    name: "Development (DE)",
    legid: "1",

    recaptcha: {
      siteKey: "6LcobusrAAAAANH9H7-CiqugwqaggkcjNMFF7ByW",
      enabled: false,
    },

    gtm: {
      containerId: "GTM-KMWRPZRF",
      enabled: false,
    },

    contact: {
      phone: "+49 30 520 151 91",
      email: "kunden@hixa.eu",
      address: "Neukirchstraße 74, 13089 Berlin",
    },

    social: {
      facebook: "https://www.facebook.com/hixa.eu",
      instagram: "https://www.instagram.com/hixa.eu",
      telegram: "https://t.me/HIXA_Tronicker_Bot",
      whatsapp: "https://api.whatsapp.com/send?phone=491734524264",
    },
  },

  // 🆕 ========== LOCALHOST UA (для розробки) ==========
  "localhost.ua": {
    domain: "localhost.ua",
    language: "ua",
    region: "UA",
    name: "Україна",
    legid: "9",

    recaptcha: {
      siteKey: "6LcobusrAAAAANH9H7-CiqugwqaggkcjNMFF7ByW",
      enabled: true,
    },

    gtm: {
      containerId: "GTM-KMWRPZRF",
      enabled: true,
    },

    contact: {
      phone: "+380936001223",
      email: "kunden@hixa.eu",
      address: "",
    },

    social: {
      facebook: "#",
      instagram: "https://www.instagram.com/hixa.ua",
      telegram: "#",
      whatsapp: "#",
    },
  },
};

// Додаємо конфігурації для різних портів (розробка)
SITE_CONFIGS["localhost:3000"] = SITE_CONFIGS["localhost"];
SITE_CONFIGS["localhost:5173"] = {
  ...SITE_CONFIGS["localhost"],
  domain: "localhost:5173",
  name: "Development (DE)",
  legid: "1",
};

// Порт 5174 - Українська
SITE_CONFIGS["localhost:5174"] = {
  ...SITE_CONFIGS["localhost.ua"],
  domain: "localhost:5174",
  name: "Development (UK)",
};

// Порт 5175 - Польська
SITE_CONFIGS["localhost:5175"] = {
  ...SITE_CONFIGS["localhost"],
  domain: "localhost:5175",
  language: "pl",
  region: "PL",
  name: "Development (PL)",
  legid: "5",
};

// Порт 5176 - Чеська
SITE_CONFIGS["localhost:5176"] = {
  ...SITE_CONFIGS["localhost"],
  domain: "localhost:5176",
  language: "cz",
  region: "CZ",
  name: "Development (CS)",
  legid: "11",
};

/**
 * Отримати конфігурацію поточного сайту
 */
export const getSiteConfig = (): SiteConfig => {
  const hostname =
    window.location.hostname +
    (window.location.port ? ":" + window.location.port : "");

  const config = SITE_CONFIGS[hostname] || SITE_CONFIGS["localhost"];

  return config;
};

/**
 * Отримати legid для CRM запитів
 */
export const getLegId = (): string => {
  const config = getSiteConfig();
  return config.legid;
};

/**
 * Отримати reCAPTCHA ключ
 */
export const getRecaptchaKey = (): string | null => {
  const config = getSiteConfig();
  return config.recaptcha.enabled ? config.recaptcha.siteKey : null;
};

/**
 * Отримати GTM контейнер ID
 */
export const getGTMContainerId = (): string | null => {
  const config = getSiteConfig();
  return config.gtm.enabled ? config.gtm.containerId : null;
};

/**
 * Отримати контактну інформацію
 */
export const getContactInfo = () => {
  const config = getSiteConfig();
  return config.contact;
};

/**
 * Отримати соціальні мережі
 */
export const getSocialLinks = () => {
  const config = getSiteConfig();
  return config.social;
};
